import { faSave, faTrash, faWindowClose } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import React, { useEffect, useState } from "react";
import { appDomain, appEmit, translate } from "../../domain/AppDomain";
import { CalendarEventStatus } from "../../domain/calendars/EnumCalendarEventStatus";
import { ICalendar } from "../../domain/calendars/ICalendar";
import { ICalendarEvent } from "../../domain/calendars/ICalendarEvent";
import { ICalendarOverride } from "../../domain/absences/ICalendarOverride";
import { BasicModal } from "../basic/BasicModal";
import { IOption, Select } from "../basic/Select";
import { TextInput } from "../basic/TextInput";
import { TimeInput } from "../basic/TimeInput";
import { EventNames } from "../../domain/events/Events";

const translateStatus = (status: CalendarEventStatus) => translate(`calendarEventStatus.${status}`);

const roundNumberBy = (number: number, roundBy: number) => Math.round(number * 1.0 / roundBy) * roundBy;
const hourMinutesString = (h: number, m: number) => `${String(Math.floor(h)).padStart(2, "0")}:${String(Math.floor(m)).padStart(2, "0")}`;
const minutesToTime = (m: number) => hourMinutesString(Math.floor(m / 60), m % 60);

export function EditAbsencePopup(props: { calendar: ICalendar, calendarEvent: ICalendarEvent, closeFn: () => void, date: Date }) {
    const { calendar, calendarEvent, closeFn, date } = props;
    const [state, setState] = useState<Partial<ICalendarOverride>>({});
    function updateState(newState: Partial<ICalendarOverride>) {
        setState({
            ...state,
            ...newState
        });
    }

    useEffect(() => {
        let startDate = new Date(date);
        let endDate = new Date(date);

        startDate.setHours(8, 0, 0);
        endDate.setHours(17, 0, 0);

        if (calendarEvent.start && calendarEvent.end) {
            startDate = calendarEvent.start;
            endDate = calendarEvent.end;
        }

        const start = hourMinutesString(startDate.getHours(), startDate.getMinutes());
        const end = hourMinutesString(endDate.getHours(), endDate.getMinutes());
        updateState({
            oid: calendarEvent.oid,
            calendarId: calendar.id,
            description: calendarEvent.description,
            startDate: startDate,
            endDate: endDate,
            start: start,
            end: end,
            status: calendarEvent.status,
            hideOtherEvents: calendarEvent.hideOtherEvents,
            isEntireDay: calendarEvent.isEntireDay
        });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [calendarEvent]);

    const statusOptions: IOption[] = [
        { value: CalendarEventStatus.OOF, label: translateStatus(CalendarEventStatus.OOF) },
        { value: CalendarEventStatus.Busy, label: translateStatus(CalendarEventStatus.Busy) },
        { value: CalendarEventStatus.WorkingElsewhere, label: translateStatus(CalendarEventStatus.WorkingElsewhere) },
        { value: CalendarEventStatus.Tentative, label: translateStatus(CalendarEventStatus.Tentative) },
        { value: CalendarEventStatus.Free, label: translateStatus(CalendarEventStatus.Free) },
    ];

    const saveEvent = async () => {
        const event = { ...state } as ICalendarOverride;
        // TODO: validate?
        await appDomain.IEmployeeAbsenceService.saveEvent(event);
        appEmit(EventNames.reloadAbsences, { changed: true });
        if (closeFn) {
            closeFn();
        }
    };

    const getNextHourTime = () => {
        var now = new Date();
        var startTimeInMinutes = (now.getHours() * 60) + now.getMinutes();
        startTimeInMinutes = roundNumberBy(startTimeInMinutes, 15);
        var endTimeInMinutes = startTimeInMinutes + 60;

        return ({
            start: minutesToTime(startTimeInMinutes),
            end: minutesToTime(endTimeInMinutes)
        });
    };

    const presetZiek = () => {
        updateState({
            status: CalendarEventStatus.OOF,
            description: "Ziek",
            isEntireDay: true,
            hideOtherEvents: true,
            ...getNextHourTime()
        });
    };

    const presetThuis = () => {
        updateState({
            status: CalendarEventStatus.WorkingElsewhere,
            description: "Werkt thuis",
            isEntireDay: true,
            hideOtherEvents: true,
            ...getNextHourTime()
        });
    };

    const presetKlant = () => {
        updateState({
            status: CalendarEventStatus.WorkingElsewhere,
            description: "Bij klant",
            isEntireDay: true,
            hideOtherEvents: true,
            ...getNextHourTime()
        });
    };

    const presetOverleg = () => {
        updateState({
            status: CalendarEventStatus.Busy,
            description: "In overleg",
            isEntireDay: false,
            hideOtherEvents: true,
            ...getNextHourTime()
        });
    };

    const getTimeFields = () => {
        if (state.isEntireDay) return;
        return <>
            <TimeInput label="Van" name="Start" value={state.start} onChange={(e) => updateState({ start: e })} />
            <TimeInput label="Tot" name="End" value={state.end} onChange={(e) => updateState({ end: e })} />
        </>;
    };

    const deleteEvent = async () => {
        if (window.confirm("Weet u het zeker?")) {
            await appDomain.IEmployeeAbsenceService.deleteEvent(state.oid!);
            closeFn();
            appEmit(EventNames.reloadAbsences, { changed: true });
        }
    };

    const getDeleteButton = () => {
        if (!state.oid) return <></>;
        return <button type="button" onClick={deleteEvent}><FontAwesomeIcon icon={faTrash} /> Verwijderen</button>
    }

    const getPresets = () => {
        if (state.oid) return <></>;
        return <>
            <div>Voorkeuze</div>
            <button type="button" onClick={presetZiek}>Ziek</button> &nbsp;
            <button type="button" onClick={presetThuis}>Werkt thuis</button> &nbsp;
            <button type="button" onClick={presetKlant}>Bij klant</button> &nbsp;
            <button type="button" onClick={presetOverleg}>In overleg</button>
        </>;
    }

    return <BasicModal show={true}>
        <h2>{calendar.name}</h2>
        {getPresets()}
        <Select className="select-box" label="Status" name="Status" value={state.status} onChange={(e) => updateState({ status: e })} options={statusOptions} />

        <TextInput label="Omschrijving" name="Description" value={state.description} onChange={(e) => updateState({ description: e })} />


        <div><input type="checkbox" name="WholeDay" checked={state.isEntireDay} onChange={() => updateState({ isEntireDay: !state.isEntireDay })} /> Hele dag</div>
        <div><input type="checkbox" name="HideCalendar" checked={state.hideOtherEvents} onChange={() => updateState({ hideOtherEvents: !state.hideOtherEvents })} /> Overlappende agenda afspraken verbergen</div>

        {getTimeFields()}

        <button type="button" onClick={closeFn}><FontAwesomeIcon icon={faWindowClose} /> Sluiten</button>
        &nbsp;
        <button type="button" onClick={saveEvent}><FontAwesomeIcon icon={faSave} /> Opslaan</button>
        &nbsp;
        {getDeleteButton()}
    </BasicModal>
};
